/**
 * DemandPdfViewer
 *
 * PDF viewer for demand documents with text search highlighting capability.
 * Uses react-pdf-highlighter-extended to display PDFs and highlight quoted text
 * from proofreading findings.
 */
import { Loader2, Search, X } from 'lucide-react';
import type { PDFDocumentProxy } from 'pdfjs-dist/types/src/display/api';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AreaHighlight,
  Highlight,
  PdfHighlighter,
  PdfHighlighterUtils,
  PdfLoader,
  useHighlightContainerContext,
} from 'react-pdf-highlighter-extended';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useGetBlobDetailQuery } from '@/services/api/blobService';
import { useGetDemandReviewWorkspaceQuery } from '@/services/api/demandReviewService';
import { TMaterializedCitationBlock } from '@/services/types/agent-chat-types';

/**
 * Text item with position information from pdfjs-dist text content
 */
interface PdfTextItem {
  str: string;
  transform: number[];
  width: number;
  height: number;
}

interface TextMatch {
  pageNumber: number;
  text: string;
  startIndex: number;
  endIndex: number;
  /** Bounding box coordinates for the match */
  boundingBox: {
    x1: number;
    y1: number;
    x2: number;
    y2: number;
  };
  /** Page dimensions for proper scaling */
  pageDimensions: {
    width: number;
    height: number;
  };
}

/** Citation highlight data passed from proofreading findings */
export interface DemandCitationHighlight {
  blocks: TMaterializedCitationBlock[];
}

interface DemandPdfViewerProps {
  matterId: string;
  workspaceId: string;
  /** Page number to navigate to (from citation click) */
  pageToNavigate?: number | null;
  /** Citation blocks to highlight (from proofreading finding click) */
  citationToHighlight?: DemandCitationHighlight | null;
  /** Callback when page navigation is complete */
  onPageNavigated?: () => void;
  /** Callback when citation highlight has been shown */
  onCitationHighlighted?: () => void;
}

/**
 * Container for rendering highlights within the PDF.
 */
const HighlightContainer = () => {
  const { highlight, isScrolledTo, highlightBindings } =
    useHighlightContainerContext<Highlight>();

  // Check if this is a citation highlight (from proofreading findings)
  const isCitationHighlight = highlight.id.startsWith('active-citation-');
  // Check if this is a search highlight
  const isSearchHighlight =
    highlight.id.startsWith('search-') || highlight.id === 'active-highlight';

  // Determine the background color based on highlight type
  let backgroundColor = 'rgba(255, 226, 143, 1)'; // Default yellow

  if (isCitationHighlight) {
    // Blue for citation highlights
    backgroundColor = isScrolledTo
      ? 'rgba(59, 130, 246, 0.5)'
      : 'rgba(59, 130, 246, 0.3)';
  } else if (isSearchHighlight) {
    // Yellow for search highlights, brighter orange for active match
    backgroundColor = isScrolledTo
      ? 'rgba(255, 165, 0, 0.7)'
      : 'rgba(255, 226, 143, 0.5)';
  }

  return (
    <div className="pointer-events-none">
      <AreaHighlight
        isScrolledTo={isScrolledTo}
        highlight={highlight}
        onChange={() => {
          // Read-only highlights
        }}
        bounds={highlightBindings.textLayer}
        style={{ background: backgroundColor }}
      />
    </div>
  );
};

/**
 * Search for text on a single page of a PDF and return matches with bounding boxes.
 */
async function searchTextInPage(
  pdfDocument: PDFDocumentProxy,
  pageNum: number,
  normalizedSearch: string,
  originalSearchText: string,
): Promise<TextMatch[]> {
  const page = await pdfDocument.getPage(pageNum);
  const textContent = await page.getTextContent();
  const viewport = page.getViewport({ scale: 1.0 });

  // Filter to text items with position data and cast to our interface
  // TextItem from pdfjs has str, transform, width, height properties
  const textItems = textContent.items.filter(
    (item) => 'str' in item && 'transform' in item,
  ) as PdfTextItem[];

  // Build a mapping of character positions to their bounding boxes
  // Each text item has a transform matrix [scaleX, skewX, skewY, scaleY, translateX, translateY]
  interface CharPosition {
    char: string;
    x: number;
    y: number;
    width: number;
    height: number;
  }

  const charPositions: CharPosition[] = [];
  let fullText = '';

  // Process each text item to extract character positions
  textItems.forEach((item) => {
    const [, , , scaleY, translateX, translateY] = item.transform;
    const itemHeight = Math.abs(scaleY) || item.height || 12;
    const itemWidth = item.width || 0;

    // Calculate character width (approximate if we have multiple chars)
    const charWidth = item.str.length > 0 ? itemWidth / item.str.length : 0;

    // Add each character with its position
    item.str.split('').forEach((char, charIndex) => {
      charPositions.push({
        char,
        x: translateX + charIndex * charWidth,
        y: translateY,
        width: charWidth,
        height: itemHeight,
      });
    });

    fullText += item.str;
  });

  // Find all occurrences of the search text
  const normalizedFullText = fullText.toLowerCase();
  const pageMatches: TextMatch[] = [];
  let startIndex = 0;
  let foundIndex = normalizedFullText.indexOf(normalizedSearch, startIndex);

  while (foundIndex !== -1) {
    const endIndex = foundIndex + originalSearchText.length;

    // Get bounding box from character positions
    if (foundIndex < charPositions.length && endIndex <= charPositions.length) {
      const matchChars = charPositions.slice(foundIndex, endIndex);

      if (matchChars.length > 0) {
        // Calculate bounding box that encompasses all matched characters
        const x1 = Math.min(...matchChars.map((c) => c.x));
        const x2 = Math.max(...matchChars.map((c) => c.x + c.width));
        // PDF coordinates have origin at bottom-left, but we need top-left for the viewer
        // The y coordinate from transform is the baseline, so we adjust
        const minY = Math.min(...matchChars.map((c) => c.y));
        const maxY = Math.max(...matchChars.map((c) => c.y + c.height));

        // Convert PDF coordinates (origin at bottom-left) to viewer coordinates (origin at top-left)
        // The library expects x1, y1, x2, y2 in the same coordinate system as width/height (pixels)
        // Formula: scaledToViewport does (viewport.width * x1) / scaled.width
        // So if we provide pixel coordinates with page dimensions, it will scale correctly
        const viewportY1 = viewport.height - maxY;
        const viewportY2 = viewport.height - minY;

        pageMatches.push({
          pageNumber: pageNum,
          text: fullText.slice(foundIndex, endIndex),
          startIndex: foundIndex,
          endIndex,
          boundingBox: {
            x1,
            y1: viewportY1,
            x2,
            y2: viewportY2,
          },
          pageDimensions: {
            width: viewport.width,
            height: viewport.height,
          },
        });
      }
    }

    startIndex = foundIndex + 1;
    foundIndex = normalizedFullText.indexOf(normalizedSearch, startIndex);
  }

  return pageMatches;
}

/**
 * Search for text across all pages of a PDF and return matches with positions.
 */
async function searchTextInPdf(
  pdfDocument: PDFDocumentProxy,
  searchText: string,
): Promise<TextMatch[]> {
  const normalizedSearch = searchText.toLowerCase().trim();

  if (!normalizedSearch) return [];

  // Create array of page numbers
  const pageNumbers = Array.from(
    { length: pdfDocument.numPages },
    (_, i) => i + 1,
  );

  // Search all pages in parallel
  const pageResults = await Promise.all(
    pageNumbers.map((pageNum) =>
      searchTextInPage(pdfDocument, pageNum, normalizedSearch, searchText),
    ),
  );

  // Flatten results
  return pageResults.flat();
}

export const DemandPdfViewer = ({
  matterId,
  workspaceId,
  pageToNavigate,
  citationToHighlight,
  onPageNavigated,
  onCitationHighlighted,
}: DemandPdfViewerProps) => {
  const highlighterUtilsRef = useRef<PdfHighlighterUtils>();
  const pdfDocumentRef = useRef<PDFDocumentProxy | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageCount, setPageCount] = useState<number>(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchHighlights, setSearchHighlights] = useState<Highlight[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [activeHighlightIndex, setActiveHighlightIndex] = useState(0);
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  // Citation highlights from proofreading findings
  const [citationHighlights, setCitationHighlights] = useState<Highlight[]>([]);
  // Track pending scroll to citation highlight
  const pendingCitationScrollRef = useRef<Highlight | null>(null);

  // Get the workspace to find the PDF blob ID
  const { data: workspace } = useGetDemandReviewWorkspaceQuery(
    { matterId, workspaceId, includeMarkdown: false },
    { skip: !matterId || !workspaceId },
  );

  const pdfBlobId = workspace?.demand_pdf_blob_id || workspace?.demand_blob_id;

  // Get the blob detail to get the download URL
  const { data: blobDetail, isLoading: isLoadingBlob } = useGetBlobDetailQuery(
    { matterId, blobId: pdfBlobId || '' },
    { skip: !matterId || !pdfBlobId },
  );

  // Fetch the PDF and create a blob URL
  useEffect(() => {
    if (!blobDetail?.download_url) {
      setPdfBlobUrl(null);
      return undefined;
    }

    let objectUrl: string | null = null;

    const fetchPdf = async () => {
      try {
        setLoadError(null);
        const response = await fetch(blobDetail.download_url);
        if (!response.ok) {
          throw new Error(`Failed to load PDF: ${response.status}`);
        }
        const blob = await response.blob();
        // Ensure the blob has the correct MIME type
        const typedBlob = new Blob([blob], { type: 'application/pdf' });
        objectUrl = window.URL.createObjectURL(typedBlob);
        setPdfBlobUrl(objectUrl);
      } catch (err) {
        setLoadError(err instanceof Error ? err.message : 'Failed to load PDF');
      }
    };

    fetchPdf();

    // Cleanup blob URL on unmount
    return () => {
      if (objectUrl) {
        window.URL.revokeObjectURL(objectUrl);
      }
    };
  }, [blobDetail?.download_url]);

  const performSearch = useCallback(async (query: string) => {
    if (!pdfDocumentRef.current || !query.trim()) {
      setSearchHighlights([]);
      return;
    }

    // Clear citation highlights when searching
    setCitationHighlights([]);
    setIsSearching(true);
    try {
      const matches = await searchTextInPdf(pdfDocumentRef.current, query);

      // Convert matches to highlights with actual bounding box coordinates
      const highlights: Highlight[] = matches.map((match, index) => ({
        id: `search-${index}`,
        position: {
          boundingRect: {
            x1: match.boundingBox.x1,
            y1: match.boundingBox.y1,
            x2: match.boundingBox.x2,
            y2: match.boundingBox.y2,
            width: match.pageDimensions.width,
            height: match.pageDimensions.height,
            pageNumber: match.pageNumber,
          },
          rects: [],
          usePdfCoordinates: false,
        },
        content: {
          text: match.text,
        },
      }));

      setSearchHighlights(highlights);
      setActiveHighlightIndex(0);

      // Scroll to first match
      if (highlights.length > 0 && highlighterUtilsRef.current) {
        highlighterUtilsRef.current.scrollToHighlight(highlights[0]);
      }
    } finally {
      setIsSearching(false);
    }
  }, []);

  const handleSearch = () => {
    performSearch(searchQuery);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setSearchHighlights([]);
    setActiveHighlightIndex(0);
  };

  const handleNextMatch = () => {
    if (searchHighlights.length === 0) return;
    const nextIndex = (activeHighlightIndex + 1) % searchHighlights.length;
    setActiveHighlightIndex(nextIndex);
    if (highlighterUtilsRef.current) {
      // Use 'active-highlight' ID since allHighlights memo will assign this ID to the active match
      const highlightToScroll = {
        ...searchHighlights[nextIndex],
        id: 'active-highlight',
      };
      highlighterUtilsRef.current.scrollToHighlight(highlightToScroll);
    }
  };

  const handlePrevMatch = () => {
    if (searchHighlights.length === 0) return;
    const prevIndex =
      (activeHighlightIndex - 1 + searchHighlights.length) %
      searchHighlights.length;
    setActiveHighlightIndex(prevIndex);
    if (highlighterUtilsRef.current) {
      // Use 'active-highlight' ID since allHighlights memo will assign this ID to the active match
      const highlightToScroll = {
        ...searchHighlights[prevIndex],
        id: 'active-highlight',
      };
      highlighterUtilsRef.current.scrollToHighlight(highlightToScroll);
    }
  };

  // Poll for current page
  useEffect(() => {
    const interval = setInterval(() => {
      const page = highlighterUtilsRef.current?.getViewer()?.currentPageNumber;
      const count = highlighterUtilsRef.current?.getViewer()?.pagesCount;
      if (page) setCurrentPage(page);
      if (count) setPageCount(count);
    }, 200);

    return () => clearInterval(interval);
  }, []);

  // Navigate to specific page when requested from citation click
  useEffect(() => {
    if (pageToNavigate && highlighterUtilsRef.current) {
      const viewer = highlighterUtilsRef.current.getViewer();
      if (viewer && viewer.pagesCount >= pageToNavigate) {
        viewer.currentPageNumber = pageToNavigate;
        // Call the callback to clear the navigation request
        onPageNavigated?.();
      }
    }
  }, [pageToNavigate, onPageNavigated]);

  // Handle citation block highlighting from proofreading findings
  useEffect(() => {
    // Only process when we have new citation data to highlight
    // Don't clear highlights when the prop becomes null - let them persist
    if (!citationToHighlight?.blocks?.length) {
      return;
    }

    // Clear any existing search highlights when showing a citation
    setSearchHighlights([]);
    setSearchQuery('');

    // Convert citation blocks to highlights
    const highlights: Highlight[] = citationToHighlight.blocks.map(
      (block, index) => ({
        id: `active-citation-${block.block_id || index}`,
        position: {
          boundingRect: {
            x1: block.bounding_box.x1,
            y1: block.bounding_box.y1,
            x2: block.bounding_box.x2,
            y2: block.bounding_box.y2,
            width: block.page_dimensions.width,
            height: block.page_dimensions.height,
            // PDF pages are 1-indexed in the viewer, blocks may be 0-indexed
            pageNumber: block.page_number + 1,
          },
          rects: [],
          usePdfCoordinates: false,
        },
        content: {
          text: `Citation block ${index + 1}`,
        },
      }),
    );

    setCitationHighlights(highlights);

    // Mark that we need to scroll to this highlight after render
    if (highlights.length > 0) {
      const [firstHighlight] = highlights;
      pendingCitationScrollRef.current = firstHighlight;
    }

    // Signal that we've processed this citation request
    onCitationHighlighted?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onCitationHighlighted is a callback for side effects only
  }, [citationToHighlight]);

  // Perform pending citation scroll after highlights have been rendered
  useEffect(() => {
    if (
      pendingCitationScrollRef.current &&
      citationHighlights.length > 0 &&
      highlighterUtilsRef.current
    ) {
      const highlightToScroll = pendingCitationScrollRef.current;
      pendingCitationScrollRef.current = null;

      // Use a timeout to ensure the PDF highlighter has processed the new highlights
      setTimeout(() => {
        highlighterUtilsRef.current?.scrollToHighlight(highlightToScroll);
      }, 200);
    }
  }, [citationHighlights]);

  // Combine search highlights with citation highlights, marking active ones
  const allHighlights = useMemo(() => {
    const searchWithActive = searchHighlights.map((h, i) => ({
      ...h,
      id: i === activeHighlightIndex ? 'active-highlight' : h.id,
    }));
    // Citation highlights already have 'active-citation-' prefix from creation
    // so they're already styled appropriately - no need to modify them
    return [...searchWithActive, ...citationHighlights];
  }, [searchHighlights, activeHighlightIndex, citationHighlights]);

  return (
    <Card className="flex h-full flex-col">
      <CardHeader className="space-y-3 pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm">Demand Document</CardTitle>
          {pageCount > 0 && (
            <span className="text-xs text-muted-foreground">
              Page {currentPage} of {pageCount}
            </span>
          )}
        </div>

        {/* Search bar */}
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search in document..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSearch();
              }}
              className="h-8 pl-8 pr-8 text-sm"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={handleClearSearch}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                aria-label="Clear search"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <Button size="sm" onClick={handleSearch} disabled={isSearching}>
            {isSearching ? 'Searching...' : 'Find'}
          </Button>
        </div>

        {/* Search results navigation */}
        {searchHighlights.length > 0 && (
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">
              {activeHighlightIndex + 1} of {searchHighlights.length} matches
            </span>
            <div className="flex gap-1">
              <Button
                size="sm"
                variant="ghost"
                onClick={handlePrevMatch}
                className="h-6 px-2"
              >
                ← Prev
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={handleNextMatch}
                className="h-6 px-2"
              >
                Next →
              </Button>
            </div>
          </div>
        )}
      </CardHeader>

      <CardContent className="flex-1 overflow-hidden p-0">
        <div className="relative h-[calc(100vh-400px)] w-full">
          {isLoadingBlob && (
            <div className="flex h-full items-center justify-center text-muted-foreground">
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              Loading document...
            </div>
          )}

          {loadError && (
            <div className="flex h-full items-center justify-center text-destructive">
              {loadError}
            </div>
          )}

          {!isLoadingBlob && !loadError && !pdfBlobUrl && (
            <div className="flex h-full items-center justify-center text-muted-foreground">
              No PDF available
            </div>
          )}

          {pdfBlobUrl && (
            <PdfLoader document={pdfBlobUrl}>
              {(pdfDocument) => {
                // Store reference to PDF document for search
                pdfDocumentRef.current = pdfDocument;

                return (
                  <PdfHighlighter
                    pdfDocument={pdfDocument}
                    highlights={allHighlights}
                    utilsRef={(utils) => {
                      highlighterUtilsRef.current = utils;
                    }}
                  >
                    <HighlightContainer />
                  </PdfHighlighter>
                );
              }}
            </PdfLoader>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
